import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { Upload } from '@creditornot/cb-icons';
import { InputAlert } from '@creditornot/cb-input';
import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { importXmenuJson } from 'apis/offer-listing-service/api';
import { ConfirmModal } from 'common/components';
import { PartialExportedMenu, readAndParseFile } from 'modules/menu/components/ImportMenuModal/utils';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { trackSelectXmenuJSONForImport } from 'modules/shared-menu/telemetry';

import { XmenuJsonDropzone } from './XmenuJsonDropzone';

type XmenuImportProps = {
  disabled?: boolean;
};

const Root = styled.div`
  padding: 0 24px 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ImportButton = styled(Button)`
  margin-block-start: 16px;
`;

const ConfirmXmenuFileModal = styled(ConfirmModal)`
  width: 500px;
`;

export const XmenuImport = ({ disabled }: XmenuImportProps) => {
  const intl = useIntl();

  const [fileName, setFileName] = useState<string | undefined>(undefined);
  const [fileError, setFileError] = useState<string | undefined>(undefined);
  const [importData, setImportData] = useState<PartialExportedMenu | undefined>(undefined);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const scope = useScopeContext();

  const handleFileChange = async (files: File[]) => {
    if (files.length > 0) {
      await readAndParseFile(files[0], intl)
        .then(menuImportData => setImportData(menuImportData))
        .then(() => trackSelectXmenuJSONForImport('Success'))
        .catch(error => {
          setFileError(error);
          trackSelectXmenuJSONForImport(`Failure: ${error}`);
        });
    }
  };

  const { mutate: executeImportXmenuFile, isLoading: importingXmenuFile } = useMutation(
    () => {
      if (!importData || scope.type !== 'venue') {
        return Promise.reject();
      }
      return importXmenuJson(scope.id, importData);
    },
    {
      onSuccess: () => {
        setIsConfirmModalOpen(false);
        window.location.reload();
        toast.success(<FormattedMessage id="listing-manager.selections.venue-editor.xmenu-import.success-message" />);
      },
      onError: () => {
        setIsConfirmModalOpen(false);
        // TODO: improve & localise error handling
        toast.error(
          'Something went wrong when creating the menu! Please check that you are using a file from Xmenu and try again.',
          { duration: Infinity },
        );
      },
    },
  );

  return (
    <Root>
      <XmenuJsonDropzone
        disabled={disabled}
        fileName={fileName}
        onDrop={files => {
          setFileError(undefined);
          setFileName(files[0].name);
          if (!files[0].name.endsWith('.json')) {
            setFileError(intl.formatMessage({ id: 'menu.fileMustBeJson' }));
            trackSelectXmenuJSONForImport('Failure: wrong file extension');
          } else {
            handleFileChange(files);
          }
        }}
        hasError={!!fileError}
        loading={false}
      />
      {!!fileError && (
        <InputAlert style={{ marginBlockStart: 8 }} variant="error">
          {fileError}
        </InputAlert>
      )}
      <ImportButton
        onClick={() => setIsConfirmModalOpen(true)}
        icon={<Upload />}
        variant="lightBlue"
        size="small"
        disabled={!importData || !!fileError || disabled}
        loading={importingXmenuFile}
      >
        <FormattedMessage id="listing-manager.selections.venue-editor.xmenu-import.import-button-label" />
      </ImportButton>

      {isConfirmModalOpen && (
        <ConfirmXmenuFileModal
          open
          header={'Import from Xmenu'}
          description={
            <Alert variant="warning" size="medium">
              <FormattedMessage
                id="listing-manager.selections.venue-editor.xmenu-import.confirmation-message"
                values={{
                  strong: (text: string) => <b>{text}</b>,
                  fileName,
                }}
              />
            </Alert>
          }
          confirmText={
            <FormattedMessage id="listing-manager.selections.venue-editor.xmenu-import.import-button-label" />
          }
          cancelText="Cancel"
          loading={importingXmenuFile}
          onConfirm={() => executeImportXmenuFile()}
          onCancel={() => setIsConfirmModalOpen(false)}
        />
      )}
    </Root>
  );
};
